import React, { useState, useEffect } from 'react';
import axios from 'axios';

function Dashboard({ user, onLogout, token, branding }) {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get('/api/documents', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setDocuments(response.data);
    } catch (err) {
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      setCategories(response.data);
    } catch (err) {
      console.error('Failed to load categories');
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const response = await axios.get(`/api/documents/${docId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download document');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getFilteredDocuments = () => {
    let filtered = documents;

    // Apply category filter first
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(doc => {
        if (selectedCategory === 'uncategorised') {
          return !doc.category || doc.category === '';
        }
        return doc.category === selectedCategory;
      });
    }

    // Then apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(doc => {
        const titleMatch = doc.title.toLowerCase().includes(search);
        const descriptionMatch = doc.description && doc.description.toLowerCase().includes(search);
        return titleMatch || descriptionMatch;
      });
    }

    return filtered;
  };

  const groupDocumentsByCategory = () => {
    const grouped = {};
    
    categories.forEach(cat => {
      grouped[cat.name] = [];
    });

    grouped['Uncategorised'] = [];

    const filteredDocs = getFilteredDocuments();

    filteredDocs.forEach(doc => {
      if (doc.category && grouped[doc.category]) {
        grouped[doc.category].push(doc);
      } else {
        grouped['Uncategorised'].push(doc);
      }
    });

    // Sort documents alphabetically within each category
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => a.title.localeCompare(b.title));
    });

    // Remove empty categories
    Object.keys(grouped).forEach(key => {
      if (grouped[key].length === 0) {
        delete grouped[key];
      }
    });

    return grouped;
  };

  const documentsByCategory = groupDocumentsByCategory();
  const filteredDocuments = getFilteredDocuments();

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
  };

  const hasActiveFilters = searchTerm.trim() !== '' || selectedCategory !== 'all';

  return (
    <div className="app-container">
      <header className="header">
        <div className="header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {branding.logo_url && (
              <img 
                src={branding.logo_url} 
                alt={branding.company_name}
                style={{
                  maxHeight: '50px',
                  height: 'auto',
                  maxWidth: '250px'
                }}
              />
            )}
            {!branding.logo_url && <div className="logo">{branding.company_name}</div>}
          </div>
          <div className="header-right">
            <div className="user-info">
              <div className="company-name">{user.companyName}</div>
              <div className="user-email">{user.email}</div>
            </div>
            <button onClick={onLogout} className="btn-logout">Logout</button>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="welcome-section">
          <h1 className="welcome-title">Health & Safety Document Library</h1>
          <p className="welcome-text">
            Welcome to your personalised health and safety management system. Below you'll find all the documentation relevant to your industry sectors. These documents are designed to help you maintain compliance and create a safer working environment for your team.
          </p>
          <div>
            <strong>Your Industry Sectors:</strong>
            <div className="sectors-list">
              {user.sectors.map(sector => (
                <span key={sector} className="sector-badge">{sector}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="documents-section">
          <div className="section-header">
            <h2 className="section-title">Available Documents</h2>
            <span className="document-count">{documents.length} documents available</span>
          </div>

          {/* Filter Controls */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '1rem',
              maxWidth: '800px',
              marginBottom: '1rem'
            }}>
              {/* Search Input */}
              <div>
                <label htmlFor="documentSearch" style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  color: '#333',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}>
                  Search Documents
                </label>
                <input
                  type="text"
                  id="documentSearch"
                  className="form-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or description..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    border: '2px solid #ddd',
                    borderRadius: '4px',
                    transition: 'border-color 0.2s'
                  }}
                />
              </div>

              {/* Category Dropdown */}
              <div>
                <label htmlFor="categoryFilter" style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  color: '#333',
                  fontSize: '0.95rem',
                  fontWeight: '500'
                }}>
                  Filter by Category
                </label>
                <select
                  id="categoryFilter"
                  className="form-input"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    border: '2px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                  ))}
                  <option value="uncategorised">Uncategorised</option>
                </select>
              </div>
            </div>

            {/* Filter Status & Clear Button */}
            {hasActiveFilters && (
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                fontSize: '0.9rem', 
                color: '#666',
                maxWidth: '800px'
              }}>
                <span>
                  Showing {filteredDocuments.length} of {documents.length} documents
                  {selectedCategory !== 'all' && ` in ${selectedCategory === 'uncategorised' ? 'Uncategorised' : selectedCategory}`}
                  {searchTerm && ` matching "${searchTerm}"`}
                </span>
                <button
                  onClick={clearAllFilters}
                  style={{
                    padding: '0.4rem 0.9rem',
                    fontSize: '0.85rem',
                    background: 'none',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: '#666',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.borderColor = 'var(--primary-color)';
                    e.target.style.color = 'var(--primary-color)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = '#ddd';
                    e.target.style.color = '#666';
                  }}
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="loading">Loading documents...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : documents.length === 0 ? (
            <div className="no-documents">
              <p>No documents are currently available for your sectors.</p>
              <p>New documents will appear here as they become available.</p>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="no-documents">
              <p>No documents found matching your filters.</p>
              <p>
                Try adjusting your search or{' '}
                <button 
                  onClick={clearAllFilters}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary-color)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: 'inherit'
                  }}
                >
                  clear all filters
                </button>
                .
              </p>
            </div>
          ) : (
            <>
              {Object.entries(documentsByCategory).map(([categoryName, docs]) => (
                <div key={categoryName} style={{ marginBottom: '3rem' }}>
                  <h3 style={{ 
                    fontSize: '1.3rem', 
                    color: '#333', 
                    marginBottom: '1rem',
                    paddingBottom: '0.5rem',
                    borderBottom: '2px solid var(--primary-color)'
                  }}>
                    {categoryName}
                  </h3>
                  <div className="documents-grid">
                    {docs.map(doc => (
                      <div key={doc.id} className="document-card">
                        <h4 className="document-title">{doc.title}</h4>
                        {doc.description && (
                          <p className="document-description">{doc.description}</p>
                        )}
                        <div className="document-meta">
                          <div>
                            <strong>Applicable to:</strong>
                            <div className="document-sectors">
                              {doc.sectors.map(sector => (
                                <span key={sector} className="document-sector">{sector}</span>
                              ))}
                            </div>
                          </div>
                          <div><strong>Uploaded:</strong> {formatDate(doc.uploadedAt)}</div>
                        </div>
                        <button 
                          onClick={() => handleDownload(doc.id, doc.filename)}
                          className="btn-download"
                        >
                          Download {doc.filename.split('.').pop().toUpperCase()}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
